import { useEffect, useMemo } from "react";
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import {
  checkAiaReadiness,
  type AiaCarInput,
  type AiaCheckCategory,
  type AiaCheckItem,
  type AiaChecklistResult,
} from "@/lib/aia-checklist";

/**
 * Pre-publish checklist for the Meta AIA feed.
 *
 * Renders the result of `checkAiaReadiness()` as grouped sections so the
 * admin sees errors (blocking), warnings (degrade targeting) and OK rows
 * at a glance. Pure UI — does not perform any side-effects.
 *
 * The parent dialog uses `onValidationChange` to disable the save button
 * when the car is being moved to status="active" but has errors, mirroring
 * the SEO editor's behaviour.
 */

const CATEGORY_LABEL: Record<AiaCheckCategory, string> = {
  required: "Påkrævede felter",
  identifiers: "Identifikatorer (VIN, trim, GTIN)",
  mapping: "Enum-mapping (Meta dropdowns)",
  mileage: "Kilometerstand & format",
  address: "Adresse & dealer-geo",
};

const CATEGORY_ORDER: AiaCheckCategory[] = [
  "required",
  "mileage",
  "mapping",
  "identifiers",
  "address",
];

export interface AiaChecklistProps {
  car: AiaCarInput;
  /** Notifies parent when readiness changes — used to gate the save button. */
  onResultChange?: (result: AiaChecklistResult) => void;
}

export function AiaChecklist({ car, onResultChange }: AiaChecklistProps) {
  const result = useMemo(() => checkAiaReadiness(car), [car]);

  // Stable callback signal so parents can sync state without effects on every
  // keystroke. We use a ref-like compare via JSON to skip no-op updates.
  useMemoizedNotify(result, onResultChange);

  const grouped = useMemo(() => groupByCategory(result.items), [result.items]);

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4">
      <header className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            Facebook AIA – publish-tjekliste
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Kontrol af felter før bilen sendes til Meta-feedet
            (<code className="rounded bg-muted px-1">/feeds/facebook</code>).
          </p>
        </div>
        <SummaryBadge result={result} />
      </header>

      <div className="space-y-3">
        {CATEGORY_ORDER.map((cat) => {
          const items = grouped.get(cat);
          if (!items || items.length === 0) return null;
          return (
            <section key={cat}>
              <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {CATEGORY_LABEL[cat]}
              </h4>
              <ul className="space-y-1">
                {items.map((item) => (
                  <ChecklistRow key={`${item.category}-${item.id}`} item={item} />
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function ChecklistRow({ item }: { item: AiaCheckItem }) {
  const tone =
    item.severity === "error"
      ? "text-destructive"
      : item.severity === "warning"
        ? "text-amber-600 dark:text-amber-400"
        : "text-emerald-600 dark:text-emerald-400";
  const Icon =
    item.severity === "error"
      ? XCircle
      : item.severity === "warning"
        ? AlertTriangle
        : CheckCircle2;
  return (
    <li className="flex items-start gap-2 text-xs">
      <Icon size={14} className={`mt-0.5 shrink-0 ${tone}`} aria-hidden />
      <div className="min-w-0 flex-1">
        <span className="font-medium text-foreground">{item.label}</span>
        {item.detail ? (
          <span className="ml-1.5 text-muted-foreground">— {item.detail}</span>
        ) : null}
      </div>
    </li>
  );
}

function SummaryBadge({ result }: { result: AiaChecklistResult }) {
  if (result.errorCount > 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2.5 py-1 text-xs font-medium text-destructive">
        <XCircle size={12} />
        {result.errorCount} fejl
        {result.warningCount > 0 ? ` · ${result.warningCount} advarsler` : ""}
      </span>
    );
  }
  if (result.warningCount > 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-400">
        <AlertTriangle size={12} />
        {result.warningCount} advarsler
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-400">
      <CheckCircle2 size={12} />
      Klar til feedet
    </span>
  );
}

function groupByCategory(items: AiaCheckItem[]) {
  const map = new Map<AiaCheckCategory, AiaCheckItem[]>();
  for (const item of items) {
    const arr = map.get(item.category) ?? [];
    arr.push(item);
    map.set(item.category, arr);
  }
  return map;
}

// Notify parent only when the *meaningful* shape of the result changes,
// not on every render. Cheap-but-correct via a JSON snapshot.
function useMemoizedNotify(
  result: AiaChecklistResult,
  onResultChange?: (r: AiaChecklistResult) => void,
) {
  const signature = useMemo(
    () =>
      JSON.stringify({
        e: result.errorCount,
        w: result.warningCount,
        ids: result.items.map((i) => `${i.id}:${i.severity}`),
      }),
    [result],
  );
  useEffect(() => {
    onResultChange?.(result);
    // signature encodes the meaningful shape of result; we intentionally
    // skip onResultChange/result in deps to avoid firing on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
}
